import React, { useState } from 'react';
import { IoIosArrowDown } from 'react-icons/io';

import type { InspectionItemValue } from '@/types/deviceReport.types';

import { calculationRules } from '@/utils/calculateValues';

import Input from '@/components/common/Input';
import Text from '@/components/common/Text';
import Spacer from '@/components/common/Spacer';

import { Theme } from '@/styles/Theme';

import * as S from './style';

interface Props {
  isEdit: boolean;
  valueInfo: InspectionItemValue[];
  setValueInfo: React.Dispatch<React.SetStateAction<InspectionItemValue[]>>;
}

const ResultItemValueList = ({ isEdit, valueInfo, setValueInfo }: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  // 렌더링을 위해 데이터 변형
  const transFormedData = transformData(valueInfo);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    templateId: number
  ) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      const updatedValueInfo = updateCalculatedValues(valueInfo, templateId, 0);
      setValueInfo(updatedValueInfo);
    } else if (!isNaN(Number(inputValue))) {
      const newValue = Number(inputValue);
      const updatedValueInfo = updateCalculatedValues(
        valueInfo,
        templateId,
        newValue
      );
      setValueInfo(updatedValueInfo);
    }
  };

  return (
    <S.Container>
      <S.TitleContainer onClick={() => setIsOpen((prev) => !prev)}>
        <Text fontSize='1.125rem' fontWeight={500}>
          설비 측정값
        </Text>
        <IoIosArrowDown
          size='1.5rem'
          color={Theme.colors.G_1}
          style={{
            transform: `rotate(${isOpen ? '180deg' : '0'})`,
          }}
        />
      </S.TitleContainer>
      {isOpen && (
        <S.DetailContainer>
          {transFormedData.map((info, idx) => (
            <div key={idx}>
              <Text
                fontSize='1.0625rem'
                color={Theme.colors.B_3}
                fontWeight={700}
              >
                {info.title
                  ? `${info.title} : ${info.subtitle}`
                  : info.subtitle}
              </Text>
              <Spacer y='0.3125rem' />
              <S.InputContainer>
                {info.values.map((value) => (
                  <Input
                    type='number'
                    name={
                      value.unit
                        ? `${value.description}(${value.unit})`
                        : value.description
                    }
                    label={
                      value.unit
                        ? `${value.description}(${value.unit})`
                        : value.description
                    }
                    key={value.template_id}
                    width='7.5rem'
                    height='2.1875rem'
                    isOnFocus
                    value={value.value}
                    onChange={(e) => handleInputChange(e, value.template_id)}
                    disabled={
                      calculationRules.hasOwnProperty(value.template_id) ||
                      isCopResultField(info.subtitle ?? '') ||
                      !isEdit
                    }
                  />
                ))}
              </S.InputContainer>
            </div>
          ))}
        </S.DetailContainer>
      )}
    </S.Container>
  );
};

export default ResultItemValueList;

interface TransformedDataItem {
  template_id: number;
  unit: string;
  value: number;
  description: string;
}

interface TransformedData {
  title?: string;
  subtitle?: string;
  values: TransformedDataItem[];
}

function transformData(data: InspectionItemValue[]): TransformedData[] {
  const result: TransformedData[] = [];
  const map: { [key: string]: TransformedData } = {};

  data.forEach((item) => {
    const parts = item.label.split('/');
    let title: string | undefined,
      subtitle: string | undefined,
      description: string;

    if (parts.length === 3) {
      // 'title/subtitle/description' 형태
      [title, subtitle, description] = parts;
    } else if (parts.length === 2) {
      // 'subtitle/description' 형태
      [subtitle, description] = parts;
      title = undefined; // title 없음
    } else {
      // 'description' 형태
      description = parts[0];
      title = undefined; // title 없음
      subtitle = undefined; // subtitle 없음
    }

    const key = title
      ? `${title}|${subtitle}`
      : subtitle
      ? `|${subtitle}`
      : `||${description}`; // title, subtitle, 또는 description 기준으로 key 생성

    if (!map[key]) {
      map[key] = { title, subtitle, values: [] };
      result.push(map[key]);
    }

    // 현재 item을 적절한 그룹에 추가
    map[key].values.push({
      template_id: item.template_id,
      unit: item.unit,
      value: item.value,
      description,
    });
  });

  return result;
}

// COP: label이 'COP/'로 시작하는 필드는 readonly (자동 계산)
const isCopResultField = (subtitle: string): boolean =>
  subtitle === 'COP' || subtitle === 'COP_HHV' || subtitle === 'COP_LHV';

// COP 자동 계산 (label 기반, 상수 하드코딩)
// 공식 기준: 260323 COP 사용열원별 시트_김팀장.xlsx
const calculateCopValues = (
  values: InspectionItemValue[]
): InspectionItemValue[] => {
  const copFields = values.filter(
    (v) => v.label.startsWith('COP/') || v.label.startsWith('COP_HHV/') || v.label.startsWith('COP_LHV/')
  );
  if (copFields.length === 0) return values;

  const getVal = (title: string, desc: string): number | null => {
    const match = values.find((v) => v.label === `${title}/${desc}`);
    return match ? match.value : null;
  };

  // 하드코딩 상수
  const density = 1000; // 냉수밀도 kg/m³
  const specificHeat = 1; // 냉수비열 kcal/kg°C
  const steamDensity = 1; // 증기밀도 kg/m³

  // Detect subtype by checking if field exists in template
  const hasField = (title: string) => values.some((v) => v.label.startsWith(`${title}/`));
  const hasCompressor = hasField('압축기동력');
  const hasSteam      = hasField('사용증기량');
  const hasMedWater   = hasField('중온수입구온도');
  const hasGas        = hasField('가스유량');

  const ratedCapacity = getVal('냉동능력(정격)', '값') ?? getVal('냉동능력(정격)', '설계값');
  const ratedEnergy = ratedCapacity != null ? ratedCapacity * 3024 : null;

  let result = [...values];

  for (const suffix of ['설계값', '측정값']) {
    // 출력 에너지 (냉수 측 계산)
    const inletT = getVal('냉수입구온도', suffix);
    const outletT = getVal('냉수출구온도', suffix);
    const flow = getVal('냉수유량', suffix);
    const calcOutput = (inletT !== null && outletT !== null && flow !== null)
      ? Math.abs(outletT - inletT) * flow * density * specificHeat
      : null;

    // ─── 전력 (Electric): Client confirmed Method A for ALL types ───
    if (hasCompressor) {
      const compressor = getVal('압축기동력', suffix);
      const inputEnergy = compressor !== null ? compressor * 860 : null;
      const output = suffix === '설계값' ? ratedEnergy : calcOutput;
      let cop = 0;
      if (output !== null && inputEnergy !== null && inputEnergy !== 0) {
        cop = Number((output / inputEnergy).toFixed(2));
      }
      result = result.map((item) =>
        item.label === `COP/${suffix}` ? { ...item, value: cop } : item
      );
    }

    // ─── 증기 (Steam): 설계=ratedEnergy, 측정=calcOutput ───
    if (hasSteam) {
      const steamFlow = getVal('사용증기량', suffix);
      const supplyEnthalpy = getVal('공급증기엔탈피', suffix);
      const condensateEnthalpy = getVal('응축수엔탈피', suffix);
      const inputEnergy = (steamFlow !== null && supplyEnthalpy !== null && condensateEnthalpy !== null)
        ? steamFlow * steamDensity * (supplyEnthalpy - condensateEnthalpy)
        : null;
      const output = suffix === '설계값' ? ratedEnergy : calcOutput;
      let cop = 0;
      if (output !== null && inputEnergy !== null && inputEnergy !== 0) {
        cop = Number((output / inputEnergy).toFixed(2));
      }
      result = result.map((item) =>
        item.label === `COP/${suffix}` ? { ...item, value: cop } : item
      );
    }

    // ─── 중온수 (Medium Temp Water): 설계=ratedEnergy, 측정=calcOutput ───
    if (hasMedWater) {
      const mwInlet = getVal('중온수입구온도', suffix);
      const mwOutlet = getVal('중온수출구온도', suffix);
      const mwFlow = getVal('중온수유량', suffix);
      const inputEnergy = (mwInlet !== null && mwOutlet !== null && mwFlow !== null)
        ? Math.abs(mwOutlet - mwInlet) * mwFlow * density * specificHeat
        : null;
      const output = suffix === '설계값' ? ratedEnergy : calcOutput;
      let cop = 0;
      if (output !== null && inputEnergy !== null && inputEnergy !== 0) {
        cop = Number((output / inputEnergy).toFixed(2));
      }
      result = result.map((item) =>
        item.label === `COP/${suffix}` ? { ...item, value: cop } : item
      );
    }

    // ─── 가스 (Gas): 설계=ratedEnergy, 측정=calcOutput, 2 results (HHV+LHV) ───
    if (hasGas) {
      const gasFlow = getVal('가스유량', suffix);
      const hhv = getVal('고위발열량', '값');
      const lhv = getVal('저위발열량', '값');
      const output = suffix === '설계값' ? ratedEnergy : calcOutput;

      // COP HHV
      const inputHHV = (gasFlow !== null && hhv !== null) ? gasFlow * hhv : null;
      let copHHV = 0;
      if (output !== null && inputHHV !== null && inputHHV !== 0) {
        copHHV = Number((output / inputHHV).toFixed(2));
      }
      result = result.map((item) =>
        item.label === `COP_HHV/${suffix}` ? { ...item, value: copHHV } : item
      );

      // COP LHV
      const inputLHV = (gasFlow !== null && lhv !== null) ? gasFlow * lhv : null;
      let copLHV = 0;
      if (output !== null && inputLHV !== null && inputLHV !== 0) {
        copLHV = Number((output / inputLHV).toFixed(2));
      }
      result = result.map((item) =>
        item.label === `COP_LHV/${suffix}` ? { ...item, value: copLHV } : item
      );
    }
  }

  return result;
};

// 변경된 input 값을 기반으로 계산값을 업데이트하는 함수
const updateCalculatedValues = (
  valueInfo: InspectionItemValue[],
  changedTemplateId: number,
  newValue: number
): InspectionItemValue[] => {
  // 입력 값 업데이트
  let updatedValues = valueInfo.map((item) =>
    item.template_id === changedTemplateId ? { ...item, value: newValue } : item
  );

  // 기존 template_id 기반 계산
  Object.entries(calculationRules).forEach(([calcTemplateId, rule]) => {
    if (rule.dependencies.includes(changedTemplateId)) {
      const calculatedValue = rule.calculate(updatedValues);
      updatedValues = updatedValues.map((item) =>
        item.template_id === parseInt(calcTemplateId)
          ? { ...item, value: calculatedValue }
          : item
      );
    }
  });

  // COP label 기반 계산
  updatedValues = calculateCopValues(updatedValues);

  return updatedValues;
};
